import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { makeRequest2 } from "../../../../utils/makeRequest";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Layout from "../../component/layout";
import styles from "./waterInvoice.module.css";
import {
  getWaterInvoice,
  getBankDetailsForLevyURL,
  getFacilityPaymentDetailsURL,
} from "../../../../utils/urls";
import { useFacilityStore } from "../../../../app/z-store/store";
import WaterInvoiceActionsDropdown from "./waterInvoiceActionsDropdown";

const WaterInvoicePage1 = () => {
  const routerLocation = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentDetails, setPaymentDetails] = useState(null); // { bank: {...} | null, mobile: {...} | null }
  const [loadingPaymentDetails, setLoadingPaymentDetails] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [facilityLogo, setFacilityLogo] = useState("");
  const [showReceiptsDialog, setShowReceiptsDialog] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { invoiceId } = location.state || {};
  const toast = useRef(null);
  const facilityId = useFacilityStore((state) => state.facilityId);

  useEffect(() => {
    if (invoiceId && facilityId) {
      fetchInvoiceData();
      fetchCompanyDetails();
      getFacilityLogo();
    } else {
      setLoading(false);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Missing invoice ID or facility ID",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, facilityId]);

  const getFacilityLogo = () => {
    try {
      const facilityFromState = routerLocation.state?.facility;
      if (facilityFromState?.logo) {
        localStorage.setItem("selectedFacilityLogo", facilityFromState.logo);
        setFacilityLogo(facilityFromState.logo);
      } else {
        const storedLogo = localStorage.getItem("selectedFacilityLogo");
        setFacilityLogo(storedLogo || "");
      }
    } catch (error) {
      console.error("Error fetching facility logo:", error);
      setFacilityLogo("");
    }
  };

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);

      const response = await makeRequest2(
        `${getWaterInvoice}/${invoiceId}?facilityId=${facilityId}`,
        "GET",
        {}
      );

      if (response.success) {
        const invoiceData = response.data?.data?.invoice || response.data?.invoice;

        if (invoiceData) {
          setInvoice(invoiceData);
          fetchPaymentDetailsForInvoice(invoiceData);
        } else {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Invoice data has an unexpected structure",
          });
        }
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: response.message || "Failed to fetch invoice data",
        });
      }
    } catch (error) {
      console.error("Error in fetchInvoiceData:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "An unexpected error occurred",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch bank and/or mobile payment details directly off the invoice's own paymentMethods.
  // Both can be enabled at once, so both are fetched in parallel and kept separate.
  const fetchPaymentDetailsForInvoice = async (invoiceData) => {
    const bankEnabled =
      invoiceData.paymentMethods?.bankPayment?.status &&
      invoiceData.paymentMethods?.bankPayment?.paymentId;
    const mobileEnabled =
      invoiceData.paymentMethods?.mobilePayment?.status &&
      invoiceData.paymentMethods?.mobilePayment?.paymentId;

    if (!invoiceData.facilityId || (!bankEnabled && !mobileEnabled)) {
      setPaymentDetails({ bank: null, mobile: null });
      return;
    }

    setLoadingPaymentDetails(true);
    const collected = { bank: null, mobile: null };
    const promises = [];

    if (bankEnabled) {
      promises.push(
        makeRequest2(
          `${getBankDetailsForLevyURL}/${invoiceData.facilityId}/${invoiceData.paymentMethods.bankPayment.paymentId}`,
          "GET"
        )
          .then((res) => {
            if (res.success) {
              const data = extractResponseData(res);
              if (data) {
                collected.bank = {
                  bankName: data.bankName,
                  accountName: data.accountName,
                  accountNumber: data.accountNumber,
                  branchName: data.branchName,
                  branchCode: data.branchCode,
                  swiftCode: data.bankCode || data.swiftCode,
                };
              }
            } else {
              toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Failed to fetch bank details",
              });
            }
          })
          .catch((err) => {
            console.error("Error fetching bank details:", err);
            toast.current?.show({
              severity: "warn",
              summary: "Warning",
              detail: "Error fetching bank details",
            });
          })
      );
    }

    if (mobileEnabled) {
      promises.push(
        makeRequest2(
          `${getFacilityPaymentDetailsURL}/${invoiceData.facilityId}/${invoiceData.paymentMethods.mobilePayment.paymentId}`,
          "GET"
        )
          .then((res) => {
            if (res.success) {
              const data = extractResponseData(res);
              if (data) {
                collected.mobile = {
                  serviceProvider: data.serviceProvider || "Mpesa",
                  paybillNumber: data.shortCode || data.paybill || data.paybillNumber,
                  tillNumber: data.tillNumber,
                  accountNumber: invoiceData.accountNumber,
                  businessName: data.businessName,
                };
              }
            } else {
              toast.current?.show({
                severity: "warn",
                summary: "Warning",
                detail: "Failed to fetch mobile payment details",
              });
            }
          })
          .catch((err) => {
            console.error("Error fetching mobile payment details:", err);
            toast.current?.show({
              severity: "warn",
              summary: "Warning",
              detail: "Error fetching mobile payment details",
            });
          })
      );
    }

    try {
      await Promise.allSettled(promises);
      setPaymentDetails(collected);
    } finally {
      setLoadingPaymentDetails(false);
    }
  };

  const extractResponseData = (response) => {
    if (!response) return null;
    if (response.success && response.data) {
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return response.data;
    }
    return response;
  };

  const fetchCompanyDetails = async () => {
    // Your existing fetchCompanyDetails implementation
    // This is just a placeholder - implement based on your needs
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById("printable-water-invoice");
    if (!element) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "PDF element not found",
      });
      return;
    }

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const filename = `Water_Invoice_${invoice?.invoiceNumber || "unknown"}_${timestamp}.pdf`;

    try {
      setLoading(true);
      toast.current?.show({
        severity: "info",
        summary: "Processing",
        detail: "Generating PDF...",
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");

      let heightLeft = imgHeight - pageHeight;
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, "", "FAST");
        heightLeft -= pageHeight;
      }

      pdf.save(filename);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "PDF downloaded successfully!",
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to generate PDF. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get the full image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    return `${imageUrl}`;
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      const day = dateObj.getDate();
      const month = dateObj.toLocaleDateString("en-US", { month: "short" });
      const year = dateObj.getFullYear();

      const getOrdinalSuffix = (day) => {
        if (day > 3 && day < 21) return "th";
        switch (day % 10) {
          case 1:
            return "st";
          case 2:
            return "nd";
          case 3:
            return "rd";
          default:
            return "th";
        }
      };

      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    const symbol = invoice?.currency?.symbol || "KSh";
    return `${symbol} ${Number(amount || 0).toFixed(2)}`;
  };

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "0.00";
    const currencyCode = invoice?.currency?.code || "KES";

    // Convert to number and format with commas
    const numericAmount = parseFloat(amount);
    const formattedNumber = numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return `${currencyCode} ${formattedNumber}`;
  };

  // Receipt viewing functionality
  const viewReceipt = (paymentData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (paymentData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", ""))
    );

    if (!isAllowedType) {
      toast.current?.show({
        severity: "info",
        summary: "Info",
        detail: "Receipt viewing is only available for cash, M-Pesa, bank transfers, and cheque payments",
      });
      return;
    }

    navigate(`/facility/levy_management/receipt/${paymentData._id}`, {
      state: {
        receipt: paymentData,
        invoice: invoice,
      },
    });
  };

  const actionBodyTemplate = (rowData) => {
    const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
    const paymentType = (rowData.type || "").toLowerCase();

    const isAllowedType = allowedTypes.some(
      (type) => paymentType === type || paymentType.includes(type.replace("-", ""))
    );

    return (
      <Button
        icon="fas fa-eye"
        className="p-button-sm p-button-text"
        onClick={() => viewReceipt(rowData)}
        disabled={!isAllowedType}
        tooltip={isAllowedType ? "View Receipt" : "Receipt not available for this payment type"}
        tooltipOptions={{ position: "left" }}
      />
    );
  };

  // Helper function to check if a value exists and is greater than 0
  const hasValue = (value) => {
    return value && Number(value) > 0;
  };

  // Calculate monthly charges (current month's consumption charges only)
  const calculateMonthlyCharges = () => {
    if (!invoice?.charges) return 0;
    const charges = invoice.charges;
    return (
      (charges.waterCharge || 0) +
      (charges.fixedCharge || 0) +
      (charges.tax || 0) +
      (charges.sewerCharge || 0)
    );
  };

  // Get balance brought forward
  const getBalanceBroughtForward = () => {
    return invoice?.balanceBroughtForward || 0;
  };

  // Calculate invoice total (monthly charges adjusted by balance brought forward)
  const calculateInvoiceTotal = () => {
    const monthlyCharges = calculateMonthlyCharges();
    const balanceBF = getBalanceBroughtForward();
    return monthlyCharges + balanceBF;
  };

  // Calculate remaining balance (what's actually due after payments)
  const calculateRemainingBalance = () => {
    const invoiceTotal = calculateInvoiceTotal();
    const amountPaid = invoice?.amountPaid || 0;
    return Math.max(0, invoiceTotal - amountPaid);
  };

  // Get total payments made
  const getTotalPayments = () => {
    return invoice?.amountPaid || 0;
  };

  // Helper function to determine balance type and styling
  const getBalanceDisplayInfo = (balance) => {
    if (balance > 0) {
      return {
        type: "credit",
        label: "Balance B/F",
        className: "debitBalance",
        sign: "-",
        description: "Credit balance from previous period",
      };
    } else if (balance < 0) {
      return {
        type: "debt",
        label: "Balance B/F",
        className: "creditBalance",
        sign: "+",
        description: "Outstanding balance from previous period",
      };
    } else {
      return {
        type: "none",
        label: "B/F",
        className: "neutralBalance",
        sign: "",
        description: "No balance brought forward",
      };
    }
  };

  // Generate dynamic table headers based on available data
  const getTableHeaders = () => {
    const headers = [
      { key: "meter", label: "Meter #", show: true },
      { key: "prevReading", label: "Prev Reading", show: true },
      { key: "currentReading", label: "Current Reading", show: true },
      { key: "usage", label: "Consumed Units", show: true },
      { key: "waterCharge", label: "Water Charge", show: hasValue(invoice?.charges?.waterCharge) },
      { key: "fixedCharge", label: "Fixed Charge", show: hasValue(invoice?.charges?.fixedCharge) },
      { key: "sewerCharge", label: "Sewer Charge", show: hasValue(invoice?.charges?.sewerCharge) },
      { key: "tax", label: "Tax", show: hasValue(invoice?.charges?.tax) },
      { key: "totalBill", label: "Monthly Charges", show: true },
    ];

    return headers.filter((header) => header.show);
  };

  // Renders bank and/or mobile payment details, each clearly labeled "Pay To:".
  // If both are enabled on the invoice, both are shown, mobile first then bank.
  const renderPaymentDetails = () => {
    if (loadingPaymentDetails) {
      return (
        <div className={styles.paymentDetails}>
          <h6 className={styles.detailsTitle}>Payment Details:</h6>
          <p>Loading payment details...</p>
        </div>
      );
    }

    const hasMobile = !!paymentDetails?.mobile;
    const hasBank = !!paymentDetails?.bank;

    if (!hasMobile && !hasBank) {
      return (
        <div className={styles.paymentDetails}>
          <h6 className={styles.detailsTitle}>Payment Details:</h6>
          <p className={styles.detailsText}>Please contact us for payment instructions</p>
        </div>
      );
    }

    return (
      <div className={styles.paymentDetails}>
        <h6 className={styles.detailsTitle}>Payment Details:</h6>

        {hasMobile && (
          <div style={{ marginBottom: hasBank ? "10px" : 0 }}>
            <h6 className={styles.detailsText} style={{ fontWeight: 700 }}>
              Pay To: {paymentDetails.mobile.serviceProvider}
            </h6>
            <h6 className={styles.detailsText}>
              Paybill: {paymentDetails.mobile.paybillNumber || paymentDetails.mobile.tillNumber || "N/A"}
            </h6>
            <h6 className={styles.detailsText}>
              Account No: {paymentDetails.mobile.accountNumber || invoice?.accountNumber || "N/A"}
            </h6>
          </div>
        )}

        {hasMobile && hasBank && <div className={styles.divider}></div>}

        {hasBank && (
          <div>
            <h6 className={styles.detailsText} style={{ fontWeight: 700 }}>
              Pay To: Bank
            </h6>
            <h6 className={styles.detailsText}>Bank: {paymentDetails.bank.bankName || "N/A"}</h6>
            <h6 className={styles.detailsText}>Account Name: {paymentDetails.bank.accountName || "N/A"}</h6>
            <h6 className={styles.detailsText}>Account Number: {paymentDetails.bank.accountNumber || "N/A"}</h6>
            <h6 className={styles.detailsText}>Branch: {paymentDetails.bank.branchName || "N/A"}</h6>
            <h6 className={styles.detailsText}>Branch Code: {paymentDetails.bank.branchCode || "N/A"}</h6>
            {paymentDetails.bank.swiftCode && (
              <h6 className={styles.detailsText}>Swift Code: {paymentDetails.bank.swiftCode}</h6>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading && !invoice) {
    return (
      <Layout>
        <Toast ref={toast} />
        <div className={styles.loading}>Loading...</div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <Toast ref={toast} />
        <div className={styles.pageContainer}>
          <div className={styles.errorMessage}>
            No invoice data available. Please check the invoice ID and try again.
          </div>
          <Link
            to="/facility/utility_management/meter_management/water_meters"
            state={{ activeTab: "invoices" }}
          >
            <i className="ti ti-arrow-narrow-left"></i> Back
          </Link>
        </div>
      </Layout>
    );
  }

  // Define read/unread status styles
  const readStatusStyles = {
    button: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0.5rem 1rem",
      borderRadius: "20px",
      fontSize: "0.875rem",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginLeft: "0.75rem",
    },
    unread: {
      backgroundColor: "#ffc107",
      color: "#212529",
    },
    read: {
      backgroundColor: "#28a745",
      color: "#ffffff",
    },
  };

  const tableHeaders = getTableHeaders();
  const balanceBF = getBalanceBroughtForward();
  const balanceInfo = getBalanceDisplayInfo(balanceBF);
  const monthlyCharges = calculateMonthlyCharges();
  const invoiceTotal = calculateInvoiceTotal();
  const remainingBalance = calculateRemainingBalance();
  const totalPayments = getTotalPayments();

  return (
    <Layout>
      <Toast ref={toast} />
      <div className={styles.pageContainer}>
        {/* Action Bar with Dropdown */}
        <div className={styles.actionCard}>
          <div className={styles.actionCardLeft}>
            <Link
              to="/facility/utility_management/meter_management/water_meters"
              state={{ activeTab: "invoices" }}
            >
              <i className="ti ti-arrow-narrow-left"></i> Back
            </Link>

            {/* Read/Unread Status Button */}
            <div style={{ display: "inline-block", marginLeft: "20px" }}>
              {invoice?.viewStatus?.isOpened === true ? (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.read,
                  }}
                >
                  <i className="fas fa-check-circle me-1"></i>
                  Read
                </button>
              ) : (
                <button
                  style={{
                    ...readStatusStyles.button,
                    ...readStatusStyles.unread,
                  }}
                >
                  <i className="fas fa-exclamation-circle me-1"></i>
                  Unread
                </button>
              )}
            </div>
          </div>
          <div className={styles.actionCardRight}>
            <WaterInvoiceActionsDropdown
              invoice={invoice}
              facilityId={facilityId}
              handleDownloadPDF={handleDownloadPDF}
              setShowReceiptsDialog={setShowReceiptsDialog}
              loading={loading}
            />
          </div>
        </div>

        {/* Receipts Dialog */}
        <Dialog
          header="Payment Receipts"
          visible={showReceiptsDialog}
          style={{ width: "50vw" }}
          onHide={() => setShowReceiptsDialog(false)}
          resizable={false}
        >
          <DataTable
            value={invoice?.reconciliationHistory || []}
            responsiveLayout="scroll"
            emptyMessage="No payment records found"
            className="p-datatable-sm"
            paginator
            rows={5}
            rowsPerPageOptions={[5, 10, 20]}
          >
            <Column field="date" header="Date" body={(rowData) => formatDate(rowData.date)} sortable />
            <Column field="amount" header="Amount" body={(rowData) => formatAmount(rowData.amount)} sortable />
            <Column
              field="type"
              header="Type"
              sortable
              body={(rowData) => {
                const paymentType = (rowData.type || "").toLowerCase();
                const allowedTypes = ["cash", "mpesa-transfer", "bank-transfer", "cheque", "payment"];
                const isAllowedType = allowedTypes.some(
                  (type) => paymentType === type || paymentType.includes(type.replace("-", ""))
                );

                let displayType = rowData.type || "Unknown";
                if (paymentType === "cash") displayType = "Cash";
                else if (paymentType === "mpesa-transfer" || paymentType.includes("mpesa")) displayType = "M-Pesa";
                else if (paymentType === "bank-transfer" || paymentType.includes("bank")) displayType = "Bank Transfer";
                else if (paymentType === "cheque" || paymentType === "check") displayType = "Cheque";
                else if (paymentType === "payment") displayType = "Payment";

                return (
                  <div>
                    {displayType}
                    {isAllowedType && (
                      <i
                        className="fas fa-receipt ml-2"
                        style={{ color: "#28a745", fontSize: "0.85em", marginLeft: "5px" }}
                        title="Receipt available"
                      />
                    )}
                  </div>
                );
              }}
            />
            <Column field="paymentReference" header="Reference" sortable />
            <Column
              field="paymentCompletion"
              header="Status"
              sortable
              body={(rowData) => (
                <span
                  className={`badge ${rowData.paymentCompletion === "Completed"
                    ? "bg-success"
                    : rowData.paymentCompletion === "Partial"
                      ? "bg-warning"
                      : rowData.paymentCompletion === "Overpaid"
                        ? "bg-info"
                        : "bg-secondary"
                    }`}
                >
                  {rowData.paymentCompletion}
                </span>
              )}
            />
            <Column body={actionBodyTemplate} exportable={false} style={{ width: "4rem" }} />
          </DataTable>
        </Dialog>

        {/* Invoice Content */}
        <div className={styles.invoiceContainer}>
          <div id="printable-water-invoice" className={styles.invoiceContent}>
            {/* Invoice Status Banner */}
            <div className={styles.statusBanner} data-status={invoice?.status?.toLowerCase()}>
              <span>{invoice?.status || "Unpaid"}</span>
            </div>

            {/* Invoice Header with logo positioning matching main invoice */}
            <div className={styles.invoiceHeader}>
              <div className={styles.invoiceHeaderContent}>
                <div className={styles.headerLeft}>
                  <h1
                    className={styles.invoiceTitleInHeader}
                    style={{
                      marginTop: "-20px",
                      marginLeft: "60%",
                      color: "white",
                      fontSize: "28px",
                      fontWeight: "600",
                    }}
                  >
                    WATER INVOICE
                  </h1>
                  {facilityLogo && (
                    <div
                      className={styles.logoContainer}
                      style={{
                        marginTop: "0",
                        marginLeft: "60%",
                      }}
                    >
                      <img src={facilityLogo} alt="Company Logo" className={styles.companyLogo} />
                    </div>
                  )}
                </div>
                <div className={styles.headerDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE NO.</span>
                    <span className={styles.value}>{invoice?.invoiceNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>ACCOUNT NO.</span>
                    <span className={styles.value}>{invoice?.accountNumber || "N/A"}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>INVOICE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dateIssued)}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.label}>DUE DATE</span>
                    <span className={styles.value}>{formatDate(invoice?.dueDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Body */}
            <div className={styles.invoiceBody}>
              {/* Address Section */}
              <div className={styles.addressSection}>
                <div className={styles.addressLayout}>
                  <div className={styles.fromSection}>
                    <h2 className={styles.sectionTitle}>FROM</h2>
                    <div className={styles.addressContent}>
                      <div>{invoice?.billerAddress?.name || companyDetails?.name || "Company Name"}</div>
                      <div>{invoice?.billerAddress?.address || companyDetails?.address || "Company Address"}</div>
                      <div>{invoice?.billerAddress?.phone || "N/A"}</div>
                    </div>
                  </div>
                  <div className={styles.billToSection}>
                    <h2 className={styles.sectionTitle}>BILL TO</h2>
                    <div className={styles.addressContent}>
                      <div>{invoice?.CustomerInfo?.fullName || "N/A"}</div>
                      <div>Unit: {invoice?.unitName || "N/A"}</div>
                      <div>Account: {invoice?.accountNumber || "N/A"}</div>
                    </div>
                  </div>
                  <div className={styles.totalSection}>
                    <h2 className={styles.sectionTitle}>TOTAL DUE</h2>
                    <div className={styles.totalAmount}>{formatCurrency(remainingBalance)}</div>
                  </div>
                </div>
              </div>

              {/* Water Meter Details - Enhanced Table */}
              <div className={styles.itemsGrid}>
                <h2 className={styles.sectionHeading}>Meter Details</h2>

                {/* Consumption Period */}
                <div className={styles.consumptionBlock}>
                  <div className={styles.detailItem}>
                    <span className={styles.infoLabel}>Consumption Period:</span>
                    <span>
                      {invoice?.consumptionPeriod
                        ? `${formatDate(invoice.consumptionPeriod.startDate)} - ${formatDate(
                          invoice.consumptionPeriod.endDate
                        )}`
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {/* Dynamic Table */}
                <div className={styles.meterDetailsTable} style={{ "--col-count": tableHeaders.length }}>
                  <div className={styles.tableHeader}>
                    {tableHeaders.map((header) => (
                      <div key={header.key} className={styles.tableHeaderCell}>
                        {header.label}
                      </div>
                    ))}
                  </div>

                  <div className={styles.tableRow}>
                    {tableHeaders.map((header) => {
                      let cellContent;

                      switch (header.key) {
                        case "meter":
                          cellContent = invoice?.meterNumber || "N/A";
                          break;
                        case "prevReading":
                          cellContent = invoice?.meterReadings?.previousReading || 0;
                          break;
                        case "currentReading":
                          cellContent = invoice?.meterReadings?.currentReading || 0;
                          break;
                        case "usage":
                          cellContent = `${invoice?.meterReadings?.usage || 0} units`;
                          break;
                        case "waterCharge":
                          cellContent = formatCurrency(invoice?.charges?.waterCharge || 0);
                          break;
                        case "fixedCharge":
                          cellContent = formatCurrency(invoice?.charges?.fixedCharge || 0);
                          break;
                        case "sewerCharge":
                          cellContent = formatCurrency(invoice?.charges?.sewerCharge || 0);
                          break;
                        case "tax":
                          cellContent = formatCurrency(invoice?.charges?.tax || 0);
                          break;
                        case "totalBill":
                          cellContent = formatCurrency(monthlyCharges);
                          break;
                        default:
                          cellContent = "N/A";
                      }

                      return (
                        <div key={header.key} className={styles.tableCell}>
                          {cellContent}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Side by side layout for Meter Image and Calculations */}
              <div className={styles.sideBySideContainer}>
                {/* Meter Image Section - Half Width */}
                {invoice?.imageUrl && (
                  <div className={styles.invoiceImageSection}>
                    <h5 className={styles.sectionHeading}>Meter Reading Photo</h5>
                    <img
                      src={getImageUrl(invoice.imageUrl)}
                      alt="Meter Reading"
                      className={styles.meterImage}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}

                {/* Enhanced Calculations Section - Half Width */}
                <div className={styles.calculations}>
                  <div className={styles.calculationsGrid}>
                    {/* Monthly Charges Row */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>MONTHLY CHARGES</span>
                      <span className={styles.calcValue}>{formatCurrency(monthlyCharges)}</span>
                    </div>

                    {/* Balance Brought Forward - Only show if it has a value */}
                    {balanceBF !== 0 && (
                      <div className={styles.calculationsRow}>
                        <span className={`${styles.calcLabel} ${styles[balanceInfo.className]}`}>
                          {balanceInfo.label}
                        </span>
                        <span className={`${styles.calcValue} ${styles[balanceInfo.className]}`}>
                          {balanceInfo.type === "credit" ? "-" : "+"}
                          {formatCurrency(Math.abs(balanceBF))}
                        </span>
                      </div>
                    )}

                    <div className={styles.divider}></div>

                    {/* Invoice Total */}
                    <div className={styles.calculationsRow}>
                      <span className={styles.calcLabel}>INVOICE TOTAL</span>
                      <span className={styles.calcValue}>{formatCurrency(invoiceTotal)}</span>
                    </div>

                    {/* Payments Made */}
                    {totalPayments > 0 && (
                      <div className={styles.calculationsRow}>
                        <span className={styles.calcLabel}>PAYMENTS MADE</span>
                        <span className={`${styles.calcValue} ${styles.creditAmount}`}>
                          -{formatCurrency(totalPayments)}
                        </span>
                      </div>
                    )}

                    <div className={styles.divider}></div>

                    {/* Final Balance Due */}
                    <div className={`${styles.calculationsRow} ${styles.balance}`}>
                      <span className={styles.calcLabel}>BALANCE DUE</span>
                      <span className={styles.calcValue}>{formatCurrency(remainingBalance)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer with proper signature like main invoice */}
              <div className={styles.footer} style={{ backgroundColor: "#f0f0f0" }}>
                <div className="row">
                  <div className="col-sm-6 mt-2 px-5">{renderPaymentDetails()}</div>

                  <div className="col-sm-6">
                    <div className={styles.signatureSection}>
                      <div className={styles.signatureBox}>
                        <img
                          src={facilityId === "685cdf7108c6b8a9e029794d" ? "/sig_vollaire.png" : "/signature.png"}
                          alt="Digital Signature"
                          style={{
                            width: "250px",
                            height: "100px",
                            objectFit: "contain",
                            marginBottom: "0px",
                            marginLeft: "25px",
                          }}
                        />
                        <p style={{ textAlign: "center", margin: "5px 0" }}>Authorized Signature</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12 pt-2">
                    <div className={styles.centeredText}>
                      <p style={{ textAlign: "center" }}>
                        To complete your payment, kindly use the provided payment details above.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WaterInvoicePage1;